import { makeStyles, tokens, Text } from '@fluentui/react-components';
import { MOBILE_BREAKPOINT } from './constants';
import { SCENARIO_STEPS } from './data';
import { ottoTokens } from '@/theme/ottoTokens';

const useStyles = makeStyles({
  section: {
    paddingBlock: '100px',
    borderTopStyle: 'solid',
    borderTopWidth: '1px',
    borderTopColor: tokens.colorNeutralStroke2,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      paddingBlock: '64px',
    },
  },
  inner: {
    maxWidth: '1200px',
    marginInline: 'auto',
    paddingInline: tokens.spacingHorizontalXXL,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      paddingInline: tokens.spacingHorizontalL,
    },
  },
  card: {
    backgroundImage: ottoTokens.landingScenarioBackground,
    backgroundColor: ottoTokens.landingCardBackground,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
    padding: '56px',
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      padding: '32px 20px',
    },
  },
  head: {
    textAlign: 'center',
    maxWidth: '760px',
    marginInline: 'auto',
    marginBlockEnd: '40px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  eyebrow: {
    display: 'block',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: ottoTokens.landingAccentTextCool,
  },
  heading: {
    display: 'block',
    fontSize: 'clamp(26px, 3.5vw, 38px)',
    fontWeight: tokens.fontWeightBold,
    lineHeight: 1.15,
    letterSpacing: '-0.02em',
    color: tokens.colorNeutralForeground1,
  },
  lead: {
    fontSize: tokens.fontSizeBase400,
    lineHeight: 1.55,
    color: tokens.colorNeutralForeground2,
    maxWidth: '660px',
    marginInline: 'auto',
  },
  steps: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: tokens.spacingHorizontalL,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      gridTemplateColumns: '1fr',
    },
  },
  step: {
    position: 'relative',
    paddingBlockStart: '40px',
  },
  stepNum: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '28px',
    height: '28px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundImage: ottoTokens.landingPrimaryGradient,
    color: tokens.colorNeutralForegroundInverted,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightBold,
  },
  stepTitle: {
    display: 'block',
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBlockEnd: tokens.spacingVerticalXS,
  },
  stepDesc: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: 1.55,
    color: tokens.colorNeutralForeground2,
  },
});

export const ScenarioSection = () => {
  const styles = useStyles();

  return (
    <section className={styles.section} aria-labelledby="scenario-title">
      <div className={styles.inner}>
        <div className={styles.card}>
          <div className={styles.head}>
            <Text className={styles.eyebrow}>How automation works in the real world</Text>
            <Text as="h2" id="scenario-title" className={styles.heading}>
              Alex automates inventory validation in an afternoon.
            </Text>
            <Text className={styles.lead}>
              Alex is a retail operations lead with no Azure experience. He has a spreadsheet from
              200 store managers and business rules that change every quarter. In a few hours, he
              ships a working agentic workflow with Logic Apps Automation — no code, no
              infrastructure, no IT ticket.
            </Text>
          </div>
          <div className={styles.steps}>
            {SCENARIO_STEPS.map((step, i) => (
              <div key={step.title} className={styles.step}>
                <span className={styles.stepNum} aria-hidden="true">{i + 1}</span>
                <Text as="h3" className={styles.stepTitle}>{step.title}</Text>
                <Text className={styles.stepDesc}>{step.desc}</Text>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
